import { useEffect, useState } from "react";
import { FinancialDocument } from "@common/types/financialDocument";
import Papa from "papaparse";
import { normalizeContentType } from "@common/helpers/contentType";
import { CSV_MIME, XLSX_MIME } from "@lib/fileDownload";
import { useFinancialDocumentFileQuery } from "./useFinancialDocumentFileQuery";
import { parseXlsx, XlsxPreviewData } from "./parseXlsx";

const CSV_PREVIEW_SIZE_LIMIT = 1_000_000;

export function useDocumentPreviewData(
    financialDocument: Pick<FinancialDocument, "id" | "fileName">,
    queryOptions?: { retry?: boolean | number },
) {
    const {
        data: response,
        isFetching,
        isError,
    } = useFinancialDocumentFileQuery(financialDocument, queryOptions);

    const contentType = normalizeContentType({
        contentType: response?.headers["content-type"],
        fileName: financialDocument.fileName,
    });
    const blob = response?.body as Blob | undefined;

    const imagePreviewAvailable = contentType.includes("image");
    const pdfPreviewAvailable = contentType === "application/pdf";
    const csvPreviewAvailable = contentType.startsWith(CSV_MIME);
    const xlsxPreviewAvailable =
        contentType.startsWith(XLSX_MIME) ||
        contentType.startsWith("application/vnd.ms-excel");

    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        if ((!imagePreviewAvailable && !pdfPreviewAvailable) || !blob) {
            setPreviewUrl(null);
            return;
        }

        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);

        return () => {
            URL.revokeObjectURL(url);
        };
    }, [imagePreviewAvailable, pdfPreviewAvailable, blob]);

    const isCsvTooLarge =
        csvPreviewAvailable && !!blob && blob.size > CSV_PREVIEW_SIZE_LIMIT;

    const [csvData, setCsvData] = useState<Array<
        Record<string, string | number>
    > | null>(null);

    useEffect(() => {
        if (!csvPreviewAvailable || !blob || isCsvTooLarge) {
            setCsvData(null);
            return;
        }

        let cancelled = false;
        blob.text()
            .then((text) => {
                if (!cancelled) {
                    const parsed = Papa.parse(text, {
                        header: true,
                        skipEmptyLines: true,
                    })?.data as Array<Record<string, string | number>>;
                    setCsvData(parsed?.length ? parsed : null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setCsvData(null);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [csvPreviewAvailable, isCsvTooLarge, blob]);

    const [xlsxData, setXlsxData] = useState<XlsxPreviewData | null>(null);

    useEffect(() => {
        if (!xlsxPreviewAvailable || !blob) {
            setXlsxData(null);
            return;
        }

        let cancelled = false;
        blob.arrayBuffer()
            .then((buffer) => {
                if (!cancelled) {
                    setXlsxData(parseXlsx(buffer));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setXlsxData(null);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [xlsxPreviewAvailable, blob]);

    return {
        isFetching,
        isError,
        imagePreviewAvailable,
        pdfPreviewAvailable,
        csvPreviewAvailable,
        isCsvTooLarge,
        xlsxPreviewAvailable,
        previewUrl,
        csvData,
        xlsxData,
    };
}
